import { useState } from 'react';
import Papa from 'papaparse';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Upload, FileText, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { nextInvoiceNumber } from '@/lib/invoiceHelpers';

// Matches the export columns a carrier's old TMS is likely to produce --
// matched by header NAME (case/punctuation-insensitive), not column
// position, so a re-ordered or slightly-renamed export still works.
// Values are arrays of normalized aliases -> the first match wins.
const HEADER_ALIASES = {
  load_number:   ['load', 'loadno', 'loadnumber', 'load#'],
  trip_number:   ['trip', 'tripno', 'tripnumber', 'trip#'],
  status:        ['status'],
  pickup_date:   ['pickup', 'pickupdate'],
  delivery_date: ['delivery', 'deliverydate'],
  driver:        ['driver', 'drivername'],
  truck:         ['truck', 'unit', 'truckunit'],
  customer:      ['customer', 'customername', 'broker'],
  origin:        ['from', 'origin', 'pickuplocation'],
  destination:   ['to', 'destination', 'deliverylocation'],
  bol_number:    ['bol', 'bolno', 'bolnumber'],
  total:         ['total', 'rate', 'amount', 'grossrevenue'],
};

const normalizeHeader = (h) => (h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Bulk CSV imports are historical/completed loads by nature -- every row
// defaults to Delivered regardless of whatever's in the CSV's own Status
// column (still shown for reference, still editable per row below) rather
// than trying to map old-TMS status vocabulary onto this app's
// Available -> Booked -> In Transit -> Delivered -> Invoiced pipeline.
const DEFAULT_IMPORT_STATUS = 'Delivered';
const CANONICAL_STATUSES = ['Available', 'Booked', 'In Transit', 'Delivered', 'Invoiced'];

// A driver/customer name from the old TMS with no match in this carrier's
// existing records. Slugified into a stable, obviously-non-functional
// placeholder email (@imported.local) so DriverProfile.user_email --
// which the rest of the app keys driver-assignment dropdowns, Settlements,
// etc. off of -- has something to reference. Collision-checked against
// `taken` (existing + already-created-this-batch emails) so two same-named
// imports don't silently collide.
const synthesizeEmail = (name, taken) => {
  const slug = (name || 'driver').toLowerCase().trim().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '') || 'driver';
  let candidate = `${slug}@imported.local`;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${slug}${n}@imported.local`;
    n++;
  }
  taken.add(candidate);
  return candidate;
};

// Accepts M/D/YYYY, M/D/YY, YYYY-MM-DD, MM-DD-YYYY -- the formats a CSV
// export is realistically going to use -- and returns YYYY-MM-DD (what
// the `date`-typed pickup_date/delivery_date columns need) or null.
const parseDate = (raw) => {
  const s = (raw || '').trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const slash = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (slash) {
    let [, m, d, y] = slash;
    if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
    const mm = m.padStart(2, '0');
    const dd = d.padStart(2, '0');
    return `${y}-${mm}-${dd}`;
  }
  return null;
};

const parseTotal = (raw) => {
  const n = parseFloat(String(raw || '').replace(/[$,]/g, ''));
  return isNaN(n) ? 0 : n;
};

export default function LoadsCsvImportModal({ onClose, onImported }) {
  const [rows, setRows] = useState(null); // parsed + mapped rows, or null before a file is chosen
  const [fileName, setFileName] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [existingLoadNumbers, setExistingLoadNumbers] = useState(new Set());
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null); // { success, failed, skipped, errors }
  const [headerInfo, setHeaderInfo] = useState(null); // { rowIndex, rawHeaders, matchedFields, skippedRows }

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setParsing(true);
    setResults(null);
    setHeaderInfo(null);
    try {
      const [driverList, existingLoads, contactList] = await Promise.all([
        client.entities.DriverProfile.list('full_name', 500),
        client.entities.Load.list('-created_date', 2000),
        client.entities.Contact.filter({ type: 'Customer' }, 'name', 1000),
      ]);
      setDrivers(driverList);
      setContacts(contactList);
      setExistingLoadNumbers(new Set(existingLoads.map(l => (l.load_number || '').trim().toLowerCase()).filter(Boolean)));

      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: (result) => {
          const allRows = result.data;

          // Don't assume row 0 is the header -- exports from Google Sheets
          // etc. often have a title row ("Loads for BP Transport") above
          // the real header row, which silently produced zero matches and
          // zero imported rows with no explanation. Score each of the
          // first several rows by how many cells match a known column
          // alias, and use whichever row scores highest as the real
          // header row.
          const scoreRow = (cells) => {
            let score = 0;
            const map = {};
            cells.forEach((cell, colIdx) => {
              const norm = normalizeHeader(cell);
              for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
                if (aliases.includes(norm) && !(field in map)) {
                  map[field] = colIdx;
                  score++;
                }
              }
            });
            return { score, map };
          };

          let best = { score: 0, map: {}, rowIndex: 0 };
          for (let i = 0; i < Math.min(allRows.length, 10); i++) {
            const { score, map } = scoreRow(allRows[i] || []);
            if (score > best.score) best = { score, map, rowIndex: i };
          }

          const headerRowRaw = allRows[best.rowIndex] || [];
          setHeaderInfo({
            rowIndex: best.rowIndex,
            rawHeaders: headerRowRaw,
            matchedFields: Object.keys(best.map),
            skippedRows: best.rowIndex,
          });

          if (best.score < 3) {
            // Too few recognized columns to trust -- surface exactly what
            // was seen instead of a generic "no rows" dead end.
            toast.error('Could not find a recognizable header row in this file.');
            setRows([]);
            setParsing(false);
            return;
          }

          const driverByName = new Map(driverList.map(d => [(d.full_name || '').trim().toLowerCase(), d]));
          const contactByName = new Map(contactList.map(c => [(c.name || '').trim().toLowerCase(), c]));
          const dataRows = allRows.slice(best.rowIndex + 1);

          const mapped = dataRows.map((cells, i) => {
            const get = (field) => (field in best.map) ? String(cells[best.map[field]] || '').trim() : '';
            const driverRaw = get('driver');
            const matchedDriver = driverRaw ? driverByName.get(driverRaw.toLowerCase()) : null;
            const customerName = get('customer');
            const matchedContact = customerName ? contactByName.get(customerName.toLowerCase()) : null;
            const loadNumber = get('load_number');
            const isDuplicate = !!loadNumber && existingLoadNumbers.has(loadNumber.trim().toLowerCase());
            return {
              _row: best.rowIndex + i + 2, // 1-indexed + header row, matches what a spreadsheet user sees
              load_number: loadNumber,
              trip_number: get('trip_number'),
              status: DEFAULT_IMPORT_STATUS, statusRaw: get('status'),
              pickup_date: parseDate(get('pickup_date')),
              delivery_date: parseDate(get('delivery_date')),
              driver_raw: driverRaw,
              matchedDriver,
              truck: get('truck'),
              customer_name: customerName,
              matchedContact,
              origin: get('origin'),
              destination: get('destination'),
              bol_number: get('bol_number'),
              total: parseTotal(get('total')),
              isDuplicate,
              include: !isDuplicate,
            };
          }).filter(r => r.load_number || r.customer_name || r.origin); // drop fully-blank trailing rows

          if (mapped.length === 0) {
            toast.error('Found a header row but no data rows under it — check the file has rows below the headers.');
          }
          setRows(mapped);
          setParsing(false);
        },
        error: (err) => {
          toast.error(`Could not read that file: ${err.message}`);
          setParsing(false);
        },
      });
    } catch (err) {
      toast.error(err?.message || 'Could not prepare import.');
      setParsing(false);
    }
  };

  const toggleRow = (i) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, include: !r.include } : r));
  const setRowStatus = (i, status) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, status } : r));

  const handleImport = async () => {
    const toImport = rows.filter(r => r.include);
    if (toImport.length === 0) {
      toast.error('No rows selected to import.');
      return;
    }
    setImporting(true);
    setProgress(0);
    const errors = [];
    let success = 0;
    let driversCreated = 0;
    let contactsCreated = 0;

    // --- Pre-pass: create any missing drivers/customers ONCE each, not
    // once per row (the same unmatched name can appear on many rows). ---
    const driverByName = new Map(drivers.map(d => [(d.full_name || '').trim().toLowerCase(), d]));
    const takenEmails = new Set(drivers.map(d => (d.user_email || '').toLowerCase()).filter(Boolean));
    const uniqueUnmatchedDrivers = [...new Set(
      toImport.filter(r => r.driver_raw && !driverByName.has(r.driver_raw.toLowerCase())).map(r => r.driver_raw)
    )];
    for (const name of uniqueUnmatchedDrivers) {
      try {
        const created = await client.entities.DriverProfile.create({
          full_name: name,
          driver_type: 'Company Driver',
          user_email: synthesizeEmail(name, takenEmails),
          approval_status: 'Approved',
          is_active: true,
          imported_from_csv: true,
        });
        driverByName.set(name.toLowerCase(), created);
        driversCreated++;
      } catch (err) {
        errors.push({ row: null, load_number: '', message: `Could not create driver "${name}": ${err?.message || 'Unknown error'}` });
      }
    }

    const contactByName = new Map(contacts.map(c => [(c.name || '').trim().toLowerCase(), c]));
    const uniqueUnmatchedCustomers = [...new Set(
      toImport.filter(r => r.customer_name && !contactByName.has(r.customer_name.toLowerCase())).map(r => r.customer_name)
    )];
    for (const name of uniqueUnmatchedCustomers) {
      try {
        const created = await client.entities.Contact.create({ name, type: 'Customer' });
        contactByName.set(name.toLowerCase(), created);
        contactsCreated++;
      } catch (err) {
        errors.push({ row: null, load_number: '', message: `Could not add "${name}" to the Address Book: ${err?.message || 'Unknown error'}` });
      }
    }

    // Running invoice list -- nextInvoiceNumber looks at the max existing
    // number, so each newly-created invoice must be appended locally
    // before computing the next one, or every imported load would collide
    // on the same invoice number.
    let invoicesSoFar = await client.entities.Invoice.list('-created_date', 2000).catch(() => []);

    // Sequential, not Promise.all -- this is a real financial-data write
    // (creates permanent Load + Invoice rows) and a partial failure needs
    // to be attributable to a specific row, not lost in a batch rejection.
    for (let i = 0; i < toImport.length; i++) {
      const r = toImport[i];
      try {
        const matchedDriver = r.driver_raw ? driverByName.get(r.driver_raw.toLowerCase()) : null;
        const matchedContact = r.customer_name ? contactByName.get(r.customer_name.toLowerCase()) : null;
        const createdLoad = await client.entities.Load.create({
          load_number: r.load_number || `IMP${Date.now().toString().slice(-6)}${i}`,
          trip_number: r.trip_number,
          status: r.status,
          pickup_date: r.pickup_date || undefined,
          delivery_date: r.delivery_date || undefined,
          assigned_driver_email: matchedDriver?.user_email || '',
          assigned_driver_name: matchedDriver?.full_name || r.driver_raw || '',
          truck_unit: r.truck,
          customer_name: r.customer_name,
          customer_email: matchedContact?.email || '',
          customer_contact_id: matchedContact?.id || '',
          origin: r.origin,
          destination: r.destination,
          bol_number: r.bol_number,
          rate: r.total,
          line_haul_rate: r.total,
          imported_from_csv: true,
        });

        const invoiceNumber = nextInvoiceNumber(invoicesSoFar);
        const createdInvoice = await client.entities.Invoice.create({
          invoice_number: invoiceNumber,
          load_id: createdLoad.id,
          load_number: createdLoad.load_number,
          customer_name: r.customer_name,
          customer_email: matchedContact?.email || '',
          total_amount: r.total,
          balance_due: r.total,
          invoiced_date: new Date().toISOString().split('T')[0],
          status: 'Unpaid',
        });
        invoicesSoFar = [...invoicesSoFar, createdInvoice];

        success++;
      } catch (err) {
        errors.push({ row: r._row, load_number: r.load_number, message: err?.message || 'Unknown error' });
      }
      setProgress(i + 1);
    }
    setImporting(false);
    setResults({
      success,
      failed: errors.length,
      skipped: rows.filter(r => !r.include).length,
      driversCreated,
      contactsCreated,
      errors,
    });
    if (success > 0) onImported();
  };

  const includedCount = rows ? rows.filter(r => r.include).length : 0;
  const unmatchedDriverCount = rows ? rows.filter(r => r.include && r.driver_raw && !r.matchedDriver).length : 0;
  const newCustomerCount = rows ? new Set(rows.filter(r => r.include && r.customer_name && !r.matchedContact).map(r => r.customer_name)).size : 0;
  const duplicateCount = rows ? rows.filter(r => r.isDuplicate).length : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={importing ? undefined : onClose} />
      <div className="flex min-h-full items-start justify-center p-4">
        <div className="relative w-full max-w-5xl my-8 p-6 space-y-5 z-10 rounded-2xl border border-border bg-background">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-space font-semibold text-foreground text-lg">Import Loads from CSV</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Expected columns: Load #, Trip #, Status, Pickup, Delivery, Driver, Truck, Customer, From, To, BOL, Total — order doesn't matter, matched by column name.
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Every row defaults to <span className="text-foreground font-medium">Delivered</span> (editable per row). Unrecognized drivers are added to Workforce and unrecognized customers to the Address Book automatically. An invoice is created for every imported load.
              </p>
            </div>
            {!importing && (
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {results ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                  <p className="text-2xl font-bold text-green-400">{results.success}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Imported</p>
                </div>
                <div className="p-4 rounded-xl bg-muted/40 border border-border text-center">
                  <p className="text-2xl font-bold text-foreground">{results.skipped}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Skipped (duplicate/unselected)</p>
                </div>
                <div className={cn("p-4 rounded-xl border text-center", results.failed > 0 ? "bg-red-500/10 border-red-500/20" : "bg-muted/40 border-border")}>
                  <p className={cn("text-2xl font-bold", results.failed > 0 ? "text-red-400" : "text-foreground")}>{results.failed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Failed</p>
                </div>
              </div>
              {(results.driversCreated > 0 || results.contactsCreated > 0) && (
                <div className="flex flex-wrap gap-2 text-xs">
                  {results.driversCreated > 0 && (
                    <span className="px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                      +{results.driversCreated} new driver{results.driversCreated === 1 ? '' : 's'} added to Workforce
                    </span>
                  )}
                  {results.contactsCreated > 0 && (
                    <span className="px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                      +{results.contactsCreated} new customer{results.contactsCreated === 1 ? '' : 's'} added to Address Book
                    </span>
                  )}
                </div>
              )}
              {results.errors.length > 0 && (
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 max-h-48 overflow-y-auto space-y-1.5">
                  {results.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-300">Row {e.row}{e.load_number ? ` (${e.load_number})` : ''}: {e.message}</p>
                  ))}
                </div>
              )}
              <Button onClick={onClose} className="w-full bg-primary text-primary-foreground">Done</Button>
            </div>
          ) : !rows ? (
            <label className={cn(
              "flex flex-col items-center justify-center gap-3 px-6 py-16 rounded-xl border-2 border-dashed cursor-pointer transition-colors",
              parsing ? "border-primary/40 bg-primary/5 pointer-events-none" : "border-border hover:border-primary/40 hover:bg-white/3"
            )}>
              <input type="file" accept=".csv" className="hidden" onChange={handleFile} disabled={parsing} />
              {parsing ? (
                <><Loader2 className="w-8 h-8 text-primary animate-spin" /><p className="text-sm text-muted-foreground">Reading {fileName}...</p></>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">Click to choose a CSV file</p>
                  <p className="text-xs text-muted-foreground">.csv exported from your previous TMS</p>
                </>
              )}
            </label>
          ) : rows.length === 0 ? (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-300">
                  {headerInfo && headerInfo.matchedFields.length < 3
                    ? "Couldn't find a header row with recognizable columns."
                    : 'Found a header row but no data rows underneath it.'}
                </div>
              </div>
              {headerInfo && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-2.5 bg-white/5 border-b border-border text-xs text-muted-foreground">
                    Row {headerInfo.rowIndex + 1} of the file, detected as the header row{headerInfo.skippedRows > 0 ? ` (${headerInfo.skippedRows} row${headerInfo.skippedRows === 1 ? '' : 's'} above it were skipped)` : ''}:
                  </div>
                  <div className="p-4 flex flex-wrap gap-1.5">
                    {headerInfo.rawHeaders.map((h, i) => {
                      const isMatched = Object.values(HEADER_ALIASES).some(aliases => aliases.includes(normalizeHeader(h)));
                      return (
                        <span key={i} className={cn(
                          "px-2 py-1 rounded-md border text-xs font-mono",
                          isMatched ? "border-green-500/40 bg-green-500/10 text-green-400" : "border-border text-muted-foreground"
                        )}>
                          {h || <em>(blank)</em>}
                        </span>
                      );
                    })}
                  </div>
                  <div className="px-4 pb-3 text-xs text-muted-foreground">
                    Green columns were recognized ({headerInfo.matchedFields.length} of 12 expected). Rename the others to match: Load #, Trip #, Status, Pickup, Delivery, Driver, Truck, Customer, From, To, BOL, Total.
                  </div>
                </div>
              )}
              <Button onClick={() => { setRows(null); setHeaderInfo(null); }} variant="outline" className="w-full border-border">Choose Different File</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <FileText className="w-3 h-3" /> {fileName} — {rows.length} rows
                  {headerInfo?.skippedRows > 0 && ` (skipped ${headerInfo.skippedRows} row${headerInfo.skippedRows === 1 ? '' : 's'} above the header)`}
                </span>
                {duplicateCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium">
                    <AlertTriangle className="w-3 h-3" /> {duplicateCount} duplicate load # already in this system — unchecked by default
                  </span>
                )}
                {unmatchedDriverCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium">
                    <AlertTriangle className="w-3 h-3" /> {unmatchedDriverCount} driver name{unmatchedDriverCount === 1 ? '' : 's'} didn't match an existing driver — will be added as a new driver
                  </span>
                )}
                {newCustomerCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium">
                    <AlertTriangle className="w-3 h-3" /> {newCustomerCount} customer{newCustomerCount === 1 ? '' : 's'} not found — will be added to your Address Book
                  </span>
                )}
              </div>

              <div className="rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto max-h-[45vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 sticky top-0">
                      <tr className="text-left text-muted-foreground">
                        <th className="px-3 py-2 w-8"></th>
                        <th className="px-3 py-2">Load #</th>
                        <th className="px-3 py-2">Trip #</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Pickup</th>
                        <th className="px-3 py-2">Delivery</th>
                        <th className="px-3 py-2">Driver</th>
                        <th className="px-3 py-2">Truck</th>
                        <th className="px-3 py-2">Customer</th>
                        <th className="px-3 py-2">From → To</th>
                        <th className="px-3 py-2">BOL</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {rows.map((r, i) => (
                        <tr key={i} className={cn(!r.include && "opacity-40")}>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={r.include} onChange={() => toggleRow(i)} className="rounded" />
                          </td>
                          <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">
                            {r.load_number || '—'}
                            {r.isDuplicate && <span className="ml-1.5 text-amber-400" title="Load # already exists">⚠</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.trip_number || '—'}</td>
                          <td className="px-3 py-2">
                            <select
                              value={r.status}
                              onChange={e => setRowStatus(i, e.target.value)}
                              className="px-2 py-1 rounded-md border text-xs bg-transparent border-border text-foreground"
                            >
                              {CANONICAL_STATUSES.map(s => <option key={s} value={s} className="bg-background text-foreground">{s}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.pickup_date || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.delivery_date || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {r.driver_raw ? (
                              <span className={cn("flex items-center gap-1", r.matchedDriver ? "text-green-400" : "text-amber-400")}>
                                {r.matchedDriver ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" /> : <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                                {r.driver_raw}
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.truck || '—'}</td>
                          <td className="px-3 py-2 text-foreground whitespace-nowrap">{r.customer_name || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.origin || '—'} → {r.destination || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.bol_number || '—'}</td>
                          <td className="px-3 py-2 text-right font-medium text-foreground whitespace-nowrap">${r.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {importing && (
                <div className="space-y-1.5">
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${(progress / includedCount) * 100}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Importing {progress} of {includedCount}...</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={handleImport} disabled={importing || includedCount === 0} className="flex-1 bg-primary text-primary-foreground">
                  {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : `Import ${includedCount} Load${includedCount === 1 ? '' : 's'}`}
                </Button>
                <Button onClick={() => setRows(null)} variant="outline" className="border-border" disabled={importing}>Choose Different File</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
